import type { InventoryItem } from '../redux/inventorySlice';
import { addDays } from '../utils/localDate';
import { formatRating } from '../utils/formatNumber';
import { rowOf } from '../components/weekly/v2/constants';
import { pantryMatcher } from './pantryMatch';
import { EXPIRING_WITHIN_DAYS, expiryIso } from './pantryFirst';
import { fitsSlotKind, narrowForSlot, type SlotFit } from './planRanking';
import { isFishRecipe, isVegetarianRecipe } from '../utils/dietClassifier';
import {
  eligibleFanRecipes,
  seededOrder,
  type FanRecipe,
  type FanRecipeSignal,
  type FanSuggestionsParams,
} from './fanSuggestionsService';

/**
 * The chips above the « Remplacer » list (#864): ask for five more, or for a
 * kind of dish, without leaving the sheet. The planner's phone sheet and the
 * desktop drawer draw the same ones through `useFanChoice`.
 *
 * A chip is its own ranked list, not the five angles inside a filter: a
 * favourite cannot read « Jamais essayé ».
 */

export type FanMeal = 'breakfast' | 'lunch' | 'dinner';

export type FanChipId =
  | 'sweet' | 'savory'
  | 'soup' | 'salad' | 'sandwich' | 'pasta'
  | 'fish' | 'veg'
  | 'quick' | 'fridge' | 'favorites';

/** The meal's own chips come first; a chip the screen cannot fit scrolls. */
const CHIPS_BY_MEAL: Record<FanMeal, FanChipId[]> = {
  breakfast: ['sweet', 'savory', 'quick', 'fridge', 'favorites'],
  lunch: ['soup', 'salad', 'sandwich', 'fridge', 'quick', 'favorites'],
  dinner: ['favorites', 'fish', 'veg', 'quick', 'fridge', 'pasta'],
};

/** Stripe of a chip's rows: its angle's colour, or the fan's warm one. */
export const FAN_CHIP_ACCENT: Record<FanChipId, string> = {
  sweet: '#c47b3c', savory: '#c47b3c',
  soup: '#c47b3c', salad: '#c47b3c', sandwich: '#c47b3c', pasta: '#c47b3c',
  fish: '#c47b3c', veg: '#c47b3c',
  quick: '#3a78a0',
  fridge: '#5d7c43',
  favorites: '#c47b3c',
};

export const FAN_PAGE_SIZE = 5;

/** A chip that cannot fill one page does not appear. */
const MIN_CHIP_RECIPES = FAN_PAGE_SIZE;

/** « Mes favoris »: the household's own word, never the publisher's rating. */
const FAVORITE_RATING = 4;

/** « Mon frigo » wants a recipe built on the shelf, not one that shares a lemon. */
const FRIDGE_MIN_ITEMS = 2;

export const fanMealOf = (sk: number): FanMeal => (['breakfast', 'lunch', 'dinner'] as const)[rowOf(sk)];

/**
 * « Rapides » has to narrow something. On an evening already capped at 30
 * minutes, 30 or less took 9 recipes out of 464.
 */
export function quickLimitFor(meal: FanMeal, slotFit?: SlotFit): number {
  if (meal === 'breakfast') return 15;
  if (meal === 'lunch') return 20;
  const cap = slotFit?.slot.maxCookTime;
  return cap != null && cap <= 30 ? 20 : 30;
}

const LETTERS = 'a-zà-ÿœ';
/** Whole words, accents included: `\b` stops at « é » and a stem is not a word. */
const wordsRe = (words: string[]) => new RegExp(`(^|[^${LETTERS}])(${words.join('|')})($|[^${LETTERS}])`, 'i');

const SOUP = wordsRe(['soupes?', 'potages?', 'veloutés?', 'chaudrées?', 'bouillons?', 'soups?', 'chowders?', 'ramen', 'pho']);
const SALAD = wordsRe(['salades?', 'salads?']);
const SANDWICH = wordsRe(['sandwichs?', 'sandwiches', 'wraps?', 'burgers?', 'pitas?', 'bagels?', 'tartines?', 'paninis?', 'guedilles?', 'croque-monsieur', 'grilled cheese']);
const PASTA = wordsRe(['pâtes', 'pasta', 'spaghettis?', 'linguine', 'penne', 'lasagnes?', 'lasagna', 'macaroni', 'fettuccine', 'fettucine', 'rigatoni', 'orecchiette', 'tagliatelles?', 'gnocchis?', 'orzo']);
const SWEET = wordsRe(['pancakes?', 'crêpes?', 'gaufres?', 'waffles?', 'muffins?', 'granola', 'gruau', 'oatmeal', 'porridge', 'scones?', 'pain doré', 'french toast', 'smoothies?', 'brioches?', 'yogourt', 'yogurt', 'banana bread', 'parfaits?', 'chia', 'céréales?']);
const SAVORY = wordsRe(['œufs?', 'oeufs?', 'eggs?', 'omelettes?', 'frittatas?', 'shakshuka', 'quiches?', 'bénédictine', 'benedict', 'bacon', 'jambon', 'ham', 'burritos?', 'bagels?', 'avocats?', 'avocado', 'saumon', 'salmon', 'asperges?', 'épinards', 'fromage', 'cheddar', 'salée?s?', 'savou?ry']);

const titleText = (r: FanRecipe) => `${r.title} ${r.translatedTitles?.fr ?? ''} ${r.translatedTitles?.en ?? ''}`.toLowerCase();

const labelled = (r: FanRecipe, label: string, words: RegExp) => r.dishTypes.includes(label) || words.test(titleText(r));

function isFavorite(signal: FanRecipeSignal | undefined): boolean {
  if ((signal?.rating ?? 0) < FAVORITE_RATING) return false;
  return !Object.values(signal?.verdicts ?? {}).some((v) => v.value === 'no');
}

const isLoved = (signal: FanRecipeSignal | undefined) =>
  Object.values(signal?.verdicts ?? {}).some((v) => v.value === 'love');

/** What the chips read, worked out once per opening. */
export interface FanShelf<T extends FanRecipe> {
  /** What the day and the week allow: the mix's pool, and every chip's but two. */
  day: T[];
  /** The same without the day's kind: « Poisson » on a vegetarian Wednesday is the cook's call. */
  anyKind: T[];
  /** The shelf items a recipe uses, read at most once per recipe. */
  usedBy: (recipe: T) => InventoryItem[];
}

export function fanShelf<T extends FanRecipe>(params: Omit<FanSuggestionsParams<T>, 'seed'>): FanShelf<T> {
  const eligible = eligibleFanRecipes(params);
  const { slotFit } = params;
  const matcher = pantryMatcher(params.inventory.filter((i) => i.status === 'active'));
  const used = new Map<string, InventoryItem[]>();
  const usedBy = (recipe: T) => {
    let items = used.get(recipe.id);
    if (!items) {
      items = matcher.itemsUsedBy(recipe);
      used.set(recipe.id, items);
    }
    return items;
  };
  if (!slotFit) return { day: eligible, anyKind: eligible, usedBy };
  const { slot, balance } = slotFit;
  const narrow = (kind: boolean) => narrowForSlot(eligible, (r) => r, slot, balance, { kind, minKeep: 3 });
  const anyKind = narrow(false);
  const day = slot.kind === 'any' ? anyKind : narrow(true);
  return { day, anyKind, usedBy };
}

type ChipParams<T extends FanRecipe> = Omit<FanSuggestionsParams<T>, 'seed'> & { sk: number };

function chipPool<T extends FanRecipe>(
  chip: FanChipId,
  shelf: FanShelf<T>,
  params: ChipParams<T>,
): T[] {
  const signalOf = (r: T) => params.metadataById[r.id];
  const quickLimit = quickLimitFor(fanMealOf(params.sk), params.slotFit);
  switch (chip) {
    case 'fish': return shelf.anyKind.filter(isFishRecipe);
    case 'veg': return shelf.anyKind.filter(isVegetarianRecipe);
    case 'quick': return shelf.day.filter((r) => r.readyInMinutes > 0 && r.readyInMinutes <= quickLimit);
    case 'fridge': return shelf.day.filter((r) => shelf.usedBy(r).length >= FRIDGE_MIN_ITEMS);
    case 'favorites': return shelf.day.filter((r) => isFavorite(signalOf(r)));
    case 'soup': return shelf.day.filter((r) => labelled(r, 'soup', SOUP));
    case 'salad': return shelf.day.filter((r) => labelled(r, 'salad', SALAD));
    case 'sandwich': return shelf.day.filter((r) => SANDWICH.test(titleText(r)));
    case 'pasta': return shelf.day.filter((r) => PASTA.test(titleText(r)));
    case 'sweet': return shelf.day.filter((r) => SWEET.test(titleText(r)) && !SAVORY.test(titleText(r)));
    case 'savory': return shelf.day.filter((r) => SAVORY.test(titleText(r)));
  }
}

export interface FanChip {
  id: FanChipId;
  labelKey: string;
}

/** The chips this meal offers on this day, each holding at least one page. */
export function fanChipsFor<T extends FanRecipe>(shelf: FanShelf<T>, params: ChipParams<T>): (FanChip & { count: number })[] {
  const kind = params.slotFit?.slot.kind;
  return CHIPS_BY_MEAL[fanMealOf(params.sk)]
    // A chip that repeats the day says nothing: the list already is that.
    .filter((id) => !(id === 'veg' && kind === 'vegetarian') && !(id === 'fish' && kind === 'fish'))
    .map((id) => ({ id, labelKey: `fanChips.${id}`, count: chipPool(id, shelf, params).length }))
    .filter((chip) => chip.count >= MIN_CHIP_RECIPES);
}

/** How many recipes « 5 nouvelles » can still bring, `chip` null being the mix. */
export function remainingFor<T extends FanRecipe>(
  chip: FanChipId | null,
  shelf: FanShelf<T>,
  params: ChipParams<T>,
  seen: string[],
): number {
  const shown = new Set(seen);
  const pool = chip ? chipPool(chip, shelf, params) : shelf.day;
  return pool.filter((r) => !shown.has(r.id)).length;
}

export interface FanChipRow<T extends FanRecipe> {
  recipe: T;
  reasonKey?: string;
  reasonParams?: Record<string, string | number>;
}

interface ChipListOptions {
  seed: number;
  /** Recipes already shown under this chip in this opening. */
  seen: string[];
  /** Local `YYYY-MM-DD`: what runs out first depends on it. */
  today: string;
  language: string;
}

/** One page of a chip's list. */
export function buildChipList<T extends FanRecipe>(
  chip: FanChipId,
  shelf: FanShelf<T>,
  params: ChipParams<T>,
  { seed, seen, today, language }: ChipListOptions,
): FanChipRow<T>[] {
  const shown = new Set(seen);
  const signalOf = (r: T) => params.metadataById[r.id];
  const order = seededOrder(seed);
  const horizon = addDays(today, EXPIRING_WITHIN_DAYS);
  const runningOut = (r: T) => shelf.usedBy(r).filter((item) => {
    const on = expiryIso(item.expiryDate);
    return on != null && on <= horizon;
  }).length;
  const byChance = (a: T, b: T) => order(a) - order(b);
  const ranking: Partial<Record<FanChipId, (a: T, b: T) => number>> = {
    favorites: (a, b) => Number(isLoved(signalOf(b))) - Number(isLoved(signalOf(a)))
      || (signalOf(b)?.rating ?? 0) - (signalOf(a)?.rating ?? 0)
      || byChance(a, b),
    fridge: (a, b) => runningOut(b) - runningOut(a)
      || shelf.usedBy(b).length - shelf.usedBy(a).length
      || byChance(a, b),
  };
  const page = chipPool(chip, shelf, params)
    .filter((r) => !shown.has(r.id))
    .sort(ranking[chip] ?? byChance)
    .slice(0, FAN_PAGE_SIZE);

  return page.map((recipe): FanChipRow<T> => {
    if (chip === 'favorites') {
      const signal = signalOf(recipe);
      return isLoved(signal)
        ? { recipe, reasonKey: 'fanChips.loved' }
        : { recipe, reasonKey: 'fanReasons.favRating', reasonParams: { rating: formatRating(signal?.rating ?? 0, language) } };
    }
    const hits = shelf.usedBy(recipe).length;
    return hits > 0
      ? { recipe, reasonKey: 'fanReasons.fridgeHits', reasonParams: { count: hits } }
      : { recipe };
  });
}
